'use client';
import type { CurrentUserDetails } from '@unifyapps/network/generated/models/currentUserDetails';
import { useEffect } from 'react';
import { setErrorReportingUser } from '../../utils/error-reporting/user';
import { useAdaptedUserDetails } from '../hooks/useAdaptedUserDetails';
import { useGetCurrentUserDetails } from '../hooks/useGetCurrentUserDetails';
import { useSetLocale } from '../hooks/useSetLocale';
import type { UserRuntimeEnvDetails } from '../nocode/types';
import { UserStoreProvider, useUserStore } from '../UserStoreProvider';

function IntialiseStore({
  isLoading,
  isLoadingContext,
  currentUserDetails,
  currentUserRuntimeEnvDetails,
}: {
  isLoading: boolean;
  isLoadingContext?: boolean;
  currentUserDetails: CurrentUserDetails | undefined;
  currentUserRuntimeEnvDetails?: UserRuntimeEnvDetails;
}) {
  const userStore = useUserStore();
  const setLocale = useSetLocale();
  const { setCurrentUserDetails, setCurrentUserRuntimeEnvDetails } = userStore.hooks.useActions();

  useEffect(() => {
    if ((!currentUserDetails && !currentUserRuntimeEnvDetails) || isLoadingContext || isLoading) {
      return;
    }

    if (currentUserDetails) {
      setLocale(currentUserDetails);
      setErrorReportingUser(currentUserDetails);
      setCurrentUserDetails(currentUserDetails);
    }
    if (currentUserRuntimeEnvDetails) {
      setCurrentUserRuntimeEnvDetails(currentUserRuntimeEnvDetails);
    }
  }, [
    currentUserDetails,
    currentUserRuntimeEnvDetails,
    isLoadingContext,
    isLoading,
    setLocale,
    setCurrentUserDetails,
    setCurrentUserRuntimeEnvDetails,
  ]);

  return null;
}

function InitialiseUserContext({
  isLoggedIn,
  children,
  initialLocale,
}: {
  isLoggedIn: boolean;
  children: React.ReactNode;
  //initial locale will set the locale the user locale when store is initialised
  //thereafter current user details will the point of truth if query is invalidated
  initialLocale?: string;
}) {
  const { currentUserDetails, isLoading } = useGetCurrentUserDetails({
    isLoggedIn,
  });

  const { userDetails: _userDetails, userRuntimeEnvDetails } = currentUserDetails ?? {};

  const userDetails = useAdaptedUserDetails({
    userDetails: _userDetails,
    initialLocale,
    isLoading,
  });

  return (
    <UserStoreProvider
      initialUserDetails={userDetails}
      initialUserRuntimeEnvDetails={userRuntimeEnvDetails}
    >
      <IntialiseStore
        currentUserDetails={userDetails}
        currentUserRuntimeEnvDetails={userRuntimeEnvDetails}
        isLoading={isLoading}
      />

      {children}
    </UserStoreProvider>
  );
}

export default InitialiseUserContext;
